import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useFormats } from '../../hooks/useFormats';

const FormatManager: React.FC = () => {
  const [formatName, setFormatName] = useState('');
  const [editingFormat, setEditingFormat] = useState<{id: string, name: string} | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const { formats, addFormat, updateFormat, deleteFormat } = useFormats();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formatName.trim()) {
      setError('フォーマット名を入力してください');
      return;
    }

    if (formats.some(format => format.name.toLowerCase() === formatName.trim().toLowerCase())) {
      setError('このフォーマット名は既に存在します');
      return;
    }

    try {
      await addFormat(formatName);
      setFormatName('');
      setSuccess(true);
    } catch (error) {
      setError('フォーマットの追加に失敗しました');
    }
  };

  const handleEdit = (format: {id: string, name: string}) => {
    setEditingFormat(format);
  };

  const handleUpdate = async () => {
    if (!editingFormat || !editingFormat.name.trim()) {
      setError('フォーマット名を入力してください');
      return;
    }

    if (formats.some(format => 
      format.id !== editingFormat.id && 
      format.name.toLowerCase() === editingFormat.name.trim().toLowerCase()
    )) {
      setError('このフォーマット名は既に存在します');
      return;
    }

    try {
      await updateFormat(editingFormat.id, editingFormat.name);
      setEditingFormat(null);
      setSuccess(true);
    } catch (error) {
      setError('フォーマットの更新に失敗しました');
    }
  };

  const handleDelete = async (formatId: string) => {
    try {
      await deleteFormat(formatId);
      setSuccess(true);
    } catch (error) {
      setError('フォーマットの削除に失敗しました');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        フォーマット管理
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          新しいフォーマットを追加
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="フォーマット名"
            value={formatName}
            onChange={(e) => setFormatName(e.target.value)}
            placeholder="例: スタンダード、モダン、コマンダー"
          />
          <Button type="submit" variant="contained">
            追加
          </Button>
        </Box>

        {success && (
          <Alert severity="success" onClose={() => setSuccess(false)}>
            フォーマットの操作が完了しました
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          登録済みフォーマット ({formats.length})
        </Typography>
        
        {formats.length === 0 ? (
          <Alert severity="info">
            まだフォーマットが登録されていません
          </Alert>
        ) : (
          <List>
            {formats.map((format) => (
              <ListItem key={format.id} divider>
                <ListItemText primary={format.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleEdit(format)}
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDelete(format.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={!!editingFormat} onClose={() => setEditingFormat(null)}>
        <DialogTitle>フォーマット名を編集</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="フォーマット名"
            value={editingFormat?.name || ''}
            onChange={(e) => setEditingFormat(prev => 
              prev ? { ...prev, name: e.target.value } : null
            )}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFormat(null)}>
            キャンセル
          </Button>
          <Button onClick={handleUpdate} variant="contained">
            更新
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormatManager;